import React from 'react';
import { 
    IconCheck, 
    IconFileText, 
    IconChevronRight,
    IconLoader
} from '../ui/Icons';

const DirectApplyModal = ({ isOpen, onClose, job, resume, documents, status, onApply }) => {
    if (!isOpen || !job) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
            <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-8">
                    <h2 className="text-2xl font-black mb-2">Direkt-Bewerbung</h2>
                    <p className="text-gray-500 mb-8">
                        Du bewirbst dich bei <span className="text-plantlix-600 font-bold">{job.sponsor_name}</span> als <span className="font-bold">{job.title}</span>.
                    </p>
                    
                    {status.success ? (
                        <div className="bg-plantlix-50 dark:bg-plantlix-500/10 p-8 rounded-3xl text-center border border-plantlix-100 dark:border-plantlix-500/20">
                            <div className="w-16 h-16 bg-plantlix-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-plantlix-500/20">
                                <IconCheck className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-black text-plantlix-800 dark:text-plantlix-400 mb-2">Bewerbung gesendet!</h3>
                            <p className="text-sm text-plantlix-700 dark:text-plantlix-500/80 mb-6">Wir haben deine Unterlagen erfolgreich übermittelt.</p>
                            <button 
                                onClick={onClose}
                                className="w-full py-4 bg-plantlix-600 text-white rounded-2xl font-black shadow-lg shadow-plantlix-600/20 transition-all hover:scale-[1.02]"
                            >
                                Schließen
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={onApply} className="space-y-6">
                            {/* Resume Info */}
                            <div className="p-4 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl flex items-center gap-4">
                                <div className="w-12 h-12 bg-white dark:bg-black/20 rounded-xl flex items-center justify-center shadow-sm">
                                    <IconFileText className="w-6 h-6 text-plantlix-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-black">Dein Online-Lebenslauf</h4>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Wird automatisch angehängt</p>
                                </div>
                                {!resume && (
                                    <span className="text-[10px] bg-red-100 text-red-600 px-2 py-1 rounded-full font-black">LEER!</span>
                                )}
                            </div>

                            {resume ? (
                                <>
                                    <div>
                                        <label className="block text-sm font-black mb-2 ml-1">Anschreiben / Kurznachricht</label>
                                        <textarea 
                                            name="cover_letter" 
                                            required 
                                            rows="5"
                                            className="w-full px-5 py-4 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-medium resize-none"
                                            placeholder="Warum bist du die richtige Wahl für diese Stelle?"
                                        ></textarea>
                                    </div>

                                    {/* Document Selection */}
                                    {documents?.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-black mb-3 ml-1">Anhänge (PDF)</label>
                                            <div className="space-y-2">
                                                {documents.map(doc => (
                                                    <label key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-xl cursor-pointer hover:bg-plantlix-50 dark:hover:bg-plantlix-500/10 transition-colors">
                                                        <input 
                                                            type="checkbox" 
                                                            name="document_ids" 
                                                            value={doc.id}
                                                            defaultChecked
                                                            className="w-4 h-4 rounded text-plantlix-600 focus:ring-plantlix-500"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="text-xs font-bold leading-none">{doc.label}</p>
                                                        </div>
                                                        <IconFileText className="w-3.5 h-3.5 text-gray-400" />
                                                    </label>
                                                ))}
                                            </div>
                                            <p className="text-[10px] text-gray-400 mt-2 ml-1 italic">* Ausgewählte Dokumente werden dieser Bewerbung beigefügt.</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="bg-amber-50 dark:bg-amber-500/10 p-6 rounded-2xl border border-amber-100 dark:border-amber-500/20 text-center">
                                    <p className="text-sm text-amber-800 dark:text-amber-400 font-bold mb-4">
                                        Bitte erstelle erst deinen Online-Lebenslauf, um dich direkt bewerben zu können.
                                    </p>
                                </div>
                            )}
                            
                            {status.error && (
                                <p className="text-red-500 text-xs font-bold text-center">{status.error}</p>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button 
                                    type="button" 
                                    onClick={onClose}
                                    className="flex-1 py-4 bg-gray-100 dark:bg-white/5 text-gray-400 rounded-2xl font-black transition-all"
                                >
                                    Abbrechen
                                </button>
                                <button 
                                    type="submit"
                                    disabled={status.loading || !resume}
                                    className="flex-[2] py-4 bg-plantlix-600 text-white rounded-2xl font-black shadow-lg shadow-plantlix-600/30 disabled:opacity-50 disabled:grayscale transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                                >
                                    {status.loading ? (
                                        <IconLoader className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>Senden <IconChevronRight className="w-5 h-5" /></>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DirectApplyModal;
